import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import {
  ChefHat,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Activity,
  Users,
  Flame,
  Eye,
  ArrowRight,
  Target,
} from 'lucide-react';

import { useKitchenAnalyticsStore } from '@/stores/kitchenAnalyticsStore';
import { cn } from '@/utils';

/**
 * Rendered for any metric with no data behind it.
 *
 * Nothing in the app populates kitchenAnalyticsStore — no loader, no
 * subscription, no endpoint. Its arrays start empty and stay empty, so
 * "Эффективность кухни 0%" was not a measurement of zero, it was the average
 * of an empty list. An em dash says "we don't know"; a 0 says "we measured,
 * and it's bad".
 */
const NO_DATA = '—';

/**
 * Card header used by every panel in this widget.
 *
 * The panels each hand-rolled `flex items-center justify-between` with a title
 * that could grow and a badge that could not shrink. At the widths this widget
 * actually gets (a third of the dashboard, four cards across), that made
 * "Живая очередь" wrap into its own "В реальном времени" badge, "Статус
 * поваров" collide with "0 активных", and "Станции" push its count up so it
 * read as a superscript. One header, one set of rules: the title truncates,
 * the badge never shrinks, and they keep a gap between them.
 */
const CardHeader = ({
  icon: Icon,
  iconClassName,
  title,
  badge,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  title: string;
  badge?: React.ReactNode;
}) => (
  <div className="flex items-start justify-between gap-2 mb-3">
    {/* items-start + a gap + a non-shrinking badge is what keeps the two apart.
        The title may wrap to a second line at narrow widths — it just can no
        longer wrap *into* the badge, which was the original defect. Wrapping
        beats truncating here: these titles are short and losing half of
        "Живая очередь" to an ellipsis is worse than a second line. */}
    <h4 className="font-semibold text-gray-900 flex items-start min-w-0">
      <Icon className={cn('w-4 h-4 mr-2 flex-shrink-0 mt-0.5', iconClassName)} />
      <span>{title}</span>
    </h4>
    {badge && <div className="flex-shrink-0 leading-6">{badge}</div>}
  </div>
);

// Sub-Component: MiniMetricCard
// This component was already well-designed and didn't need translation changes,
// as it receives its labels from its parent.
const MiniMetricCard = ({
  label,
  value,
  unit,
  icon: Icon,
  color = 'blue',
  size = 'normal'
}: {
  label: string;
  value: string | number;
  unit?: string;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  size?: 'normal' | 'compact';
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
  };

  // The trend arrow and delta line are gone. They were computed as
  // `totalOrders - 10` against a baseline of 10 that exists nowhere, and two
  // literals: '-2' minutes and '+3%' today. A card cannot show a change over
  // time when nothing records what the value was before.
  return (
    <div className={cn(
      'rounded-lg border transition-all hover:shadow-sm',
      colorClasses[color],
      size === 'compact' ? 'p-3' : 'p-4'
    )}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={cn('flex-shrink-0', size === 'compact' ? 'w-4 h-4' : 'w-5 h-5')} />
      </div>

      <div className="space-y-1">
        <div className="flex items-baseline space-x-1">
          <span className={cn('font-bold', size === 'compact' ? 'text-lg' : 'text-xl')}>
            {value}
          </span>
          {/* No unit beside an em dash — "— %" reads as a broken number. */}
          {unit && value !== NO_DATA && (
            <span className={cn('text-current opacity-70', size === 'compact' ? 'text-xs' : 'text-sm')}>
              {unit}
            </span>
          )}
        </div>

        <div className={cn('text-current opacity-80', size === 'compact' ? 'text-xs' : 'text-sm')}>
          {label}
        </div>
      </div>
    </div>
  );
};


// Sub-Component: LiveOrderStatus
const LiveOrderStatus = ({ orders, t }: { orders: any[]; t: TFunction }) => {
  const statusCounts = {
    pending: orders.filter(o => o.status === 'pending').length,
    preparing: orders.filter(o => o.status === 'preparing').length,
    ready: orders.filter(o => o.status === 'ready').length,
    delayed: orders.filter(o => o.isDelayed).length,
  };

  const hasData = orders.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
      <CardHeader
        icon={Activity}
        iconClassName="text-blue-500"
        title={t('kitchenWidget.liveQueue.title')}
      />

      <div className="grid grid-cols-2 gap-3">
        <div className="text-center">
          <div className="text-lg font-bold text-yellow-600">{hasData ? statusCounts.pending : NO_DATA}</div>
          <div className="text-xs text-gray-600">{t('kitchenWidget.liveQueue.pending')}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600">{hasData ? statusCounts.preparing : NO_DATA}</div>
          <div className="text-xs text-gray-600">{t('kitchenWidget.liveQueue.preparing')}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">{hasData ? statusCounts.ready : NO_DATA}</div>
          <div className="text-xs text-gray-600">{t('kitchenWidget.liveQueue.ready')}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-red-600">{hasData ? statusCounts.delayed : NO_DATA}</div>
          <div className="text-xs text-gray-600">{t('kitchenWidget.liveQueue.delayed')}</div>
        </div>
      </div>
    </div>
  );
};

// Sub-Component: ChefPerformanceSummary
const ChefPerformanceSummary = ({ chefs, t }: { chefs: any[]; t: TFunction }) => {
  const activeChefs = chefs.filter(chef => chef.status === 'active');
  const averageEfficiency = activeChefs.length > 0 ? activeChefs.reduce((acc, chef) => acc + chef.efficiency, 0) / activeChefs.length : 0;
  const topPerformer = chefs.length > 0 ? chefs.reduce((best, chef) => chef.efficiency > best.efficiency ? chef : best, chefs[0]) : null;

  const hasData = chefs.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
      <CardHeader
        icon={Users}
        iconClassName="text-purple-500"
        title={t('kitchenWidget.chefStatus.title')}
        badge={
          <span className="text-xs text-gray-600">
            {hasData ? t('kitchenWidget.chefStatus.active_plural', { count: activeChefs.length }) : NO_DATA}
          </span>
        }
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-600 min-w-0 truncate">{t('kitchenWidget.chefStatus.avgEfficiency')}</span>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* The track only appears when there is a figure to fill it.
                With no chef data it rendered as an empty grey bar beside 0%,
                which reads as a real measurement that happens to be zero. */}
            {hasData && (
              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full transition-all',
                    averageEfficiency >= 85 ? 'bg-green-500' :
                    averageEfficiency >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                  )}
                  style={{ width: `${averageEfficiency}%` }}
                />
              </div>
            )}
            <span className="text-sm font-semibold text-gray-900">
              {hasData ? `${Math.round(averageEfficiency)}%` : NO_DATA}
            </span>
          </div>
        </div>

        {topPerformer && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">{t('kitchenWidget.chefStatus.topPerformer')}</span>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{topPerformer.name}</div>
                <div className="text-xs text-green-600">{t('kitchenWidget.chefStatus.efficiency_percentage', { efficiency: topPerformer.efficiency })}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Sub-Component: AlertSummary
const AlertSummary = ({ alerts, t }: { alerts: any[]; t: TFunction }) => {
    const activeAlerts = alerts.filter(alert => !alert.resolved);
    const criticalAlerts = activeAlerts.filter(alert => alert.severity === 'critical').length;
    const highAlerts = activeAlerts.filter(alert => alert.severity === 'high').length;
  
    // Same as the dashboard's alert panel: nothing appends to bottleneckAlerts,
    // so an empty list means "not monitored", not "optimal".
    if (activeAlerts.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
          <CardHeader
            icon={AlertTriangle}
            iconClassName="text-gray-400"
            title={t('kitchenWidget.alerts.title')}
          />
          <p className="text-xs text-gray-500">{t('kitchenWidget.alerts.noMonitoring')}</p>
        </div>
      );
    }
  
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
        <CardHeader
          icon={AlertTriangle}
          iconClassName="text-orange-500"
          title={t('kitchenWidget.alerts.title')}
          badge={
            <span className="text-xs text-gray-600">
              {t('kitchenWidget.alerts.total_plural', { count: activeAlerts.length })}
            </span>
          }
        />

        <div className="space-y-2">
          {criticalAlerts > 0 && (
            <div className="flex items-center justify-between p-2 bg-red-50 rounded">
              <span className="text-sm text-red-700">{t('kitchenWidget.alerts.critical')}</span>
              <span className="text-sm font-bold text-red-800">{criticalAlerts}</span>
            </div>
          )}
          
          {highAlerts > 0 && (
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
              <span className="text-sm text-orange-700">{t('kitchenWidget.alerts.highPriority')}</span>
              <span className="text-sm font-bold text-orange-800">{highAlerts}</span>
            </div>
          )}
  
          <div className="pt-1">
            <div className="text-xs text-gray-600">
              {t('kitchenWidget.alerts.latest')}: {activeAlerts[0]?.description?.startsWith('admin.') ? t(activeAlerts[0].description) : activeAlerts[0]?.description || t('kitchenWidget.alerts.noAlerts')}
            </div>
          </div>
        </div>
      </div>
    );
  };


// Main Kitchen Monitor Widget for Admin Dashboard
export const KitchenMonitorWidget: React.FC<{ 
  variant?: 'compact' | 'expanded';
  showHeader?: boolean;
}> = ({ 
  variant = 'expanded',
  showHeader = true 
}) => {
  const { t } = useTranslation();
  const {
    activeOrders,
    chefPerformance,
    kitchenStations,
    bottleneckAlerts,
  } = useKitchenAnalyticsStore();

  // Each figure is shown only when its own source has data. `isLiveMode`
  // defaults to true in the store, so the header used to show a pulsing green
  // "В реальном времени" dot and an "Обновлено HH:MM:SS" that ticked every
  // five seconds off a setInterval — a clock, not a refresh. Nothing was being
  // fetched. Both are gone until something actually streams in.
  const hasOrderData = activeOrders.length > 0;
  const hasChefData = chefPerformance.length > 0;

  const totalOrders = activeOrders.length;
  const delayedOrders = activeOrders.filter(o => o.isDelayed).length;
  const averageWaitTime = hasOrderData
    ? Math.round(activeOrders.reduce((acc, order) => acc + order.customerWaitTime, 0) / totalOrders)
    : null;
  const activeChefs = chefPerformance.filter(chef => chef.status === 'active').length;
  const averageEfficiency = hasChefData
    ? Math.round(chefPerformance.reduce((acc, chef) => acc + chef.efficiency, 0) / chefPerformance.length)
    : null;

  const efficiencyColor = (value: number | null) =>
    value === null ? 'blue' as const
      : value >= 85 ? 'green' as const
      : value >= 75 ? 'orange' as const
      : 'red' as const;

  // Compact variant view
  if (variant === 'compact') {
    return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-soft border border-neutral-200 p-4"
        >
          {showHeader && (
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <ChefHat className="w-5 h-5 mr-2 text-blue-500" />
                {t('kitchenWidget.compactTitle')}
              </h3>
              <Link 
                to="/admin/kitchen-monitoring"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Eye className="w-4 h-4" />
              </Link>
            </div>
          )}
  
          <div className="grid grid-cols-2 gap-3">
            <MiniMetricCard
              label={t('admin.kitchen.activeOrders')}
              value={hasOrderData ? totalOrders : NO_DATA}
              icon={Activity}
              color="blue"
              size="compact"
            />
            <MiniMetricCard
              label={t('admin.kitchen.efficiency')}
              value={averageEfficiency ?? NO_DATA}
              unit="%"
              icon={Target}
              color={efficiencyColor(averageEfficiency)}
              size="compact"
            />
          </div>
  
          {delayedOrders > 0 && (
            <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700">{t('kitchenWidget.delayedOrders_plural', { count: delayedOrders })}</span>
                <span className="text-sm font-bold text-red-800">{delayedOrders}</span>
              </div>
            </div>
          )}
        </motion.div>
      );
  }

  // Expanded variant view (this is the one from the screenshot)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft border border-neutral-200"
    >
      {showHeader && (
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChefHat className="w-6 h-6 mr-2 text-blue-500" />
              {t('kitchenWidget.title')}
            </h3>
          </div>

          <Link
            to="/admin/kitchen-monitoring"
            className="flex items-center space-x-2 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <span className="text-sm font-medium">{t('kitchenWidget.viewFullDashboard')}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="p-6">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MiniMetricCard
            label={t('admin.kitchen.activeOrders')}
            value={hasOrderData ? totalOrders : NO_DATA}
            icon={Activity}
            color="blue"
          />

          <MiniMetricCard
            label={t('admin.kitchen.avgWaitTime')}
            value={averageWaitTime ?? NO_DATA}
            unit={t('units.min')}
            icon={Clock}
            color={
              averageWaitTime === null ? 'blue'
                : averageWaitTime > 15 ? 'red'
                : averageWaitTime > 10 ? 'orange'
                : 'green'
            }
          />

          <MiniMetricCard
            label={t('admin.kitchen.kitchenEfficiency')}
            value={averageEfficiency ?? NO_DATA}
            unit="%"
            icon={Target}
            color={efficiencyColor(averageEfficiency)}
          />

          {/* "Все станции покрыты" used to sit under this number as a standing
              claim, regardless of how many chefs or stations were reported. */}
          <MiniMetricCard
            label={t('admin.kitchen.activeChefs')}
            value={hasChefData ? activeChefs : NO_DATA}
            icon={Users}
            color="purple"
          />
        </div>

        {/* Status Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <LiveOrderStatus orders={activeOrders} t={t} />
          <ChefPerformanceSummary chefs={chefPerformance} t={t} />
          <AlertSummary alerts={bottleneckAlerts} t={t} />
          
          {/* Station Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
            <CardHeader
              icon={Flame}
              iconClassName="text-orange-500"
              title={t('kitchenWidget.stations.title')}
              badge={
                <span className="text-xs text-gray-600">
                  {kitchenStations.length > 0
                    ? t('kitchenWidget.stations.total_plural', { count: kitchenStations.length })
                    : NO_DATA}
                </span>
              }
            />

            <div className="space-y-2">
              {kitchenStations.slice(0, 3).map((station) => (
                <div key={station.id} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-600 truncate min-w-0">{station.name}</span>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={cn('h-full transition-all', station.utilizationRate > 85 ? 'bg-red-500' : station.utilizationRate > 70 ? 'bg-yellow-500' : 'bg-green-500')}
                        style={{ width: `${station.utilizationRate}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 w-8 text-right">
                      {station.utilizationRate}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Urgent Alerts Banner */}
        {delayedOrders > 0 && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-red-800">
                  {t('kitchenWidget.delayedOrders_plural', { count: delayedOrders })}
                </span>
              </div>
              <Link to="/admin/kitchen-monitoring" className="text-sm text-red-600 hover:text-red-800 font-medium">
                {t('kitchenWidget.viewDetails')} →
              </Link>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};